































































































































































import VueElementLoading from "vue-element-loading"
import VueDraggable from 'vuedraggable'
import { Vue, Component, Prop } from 'vue-property-decorator'
import { nameMap } from '@/modules/budget/monitoring/reports-constructor/common/nameMap'
import { cloneDeep } from "lodash"
import { Classificator } from "../../../common/types/Classificator"
import { Indicator } from "../../../common/types/Indicator"
import { formatDigitsGrouping, padLeadingZeros } from "../../../common/utils/formattingUtils"
import { Report127Row, Report127TableRow, SectionSeparator } from "../table-data/table-data-types"
import { ExpenseType } from "../../../common/types/roots/fields"
import { vue as i18n } from "@/services/i18n"

interface Section {
    key: string
    nameRu: string
    nameKk: string
    value: any[]
    /** Части отчёта внутри раздела переключателя (у регионов и «Все» — пусто) */
    parts: ReportPart[]
}

/**
 * Часть отчёта (I, II, ..., VII, «Справочно»).
 * Обычно раздел переключателя = одна часть, но раздел «V–VII» объединяет четыре.
 */
interface ReportPart {
    key: string
    nameRu: string
    nameKk: string
    /** Часть начинается со своей строки-итога (например, «II. ЗАТРАТЫ») — отдельный заголовок перед ней не нужен */
    hasOwnTitleRow: boolean
    /** Часть собрана из строк раздела без распознанной строки-итога; названа именем раздела */
    isSectionFallback: boolean
    rows: Report127TableRow[]
}

/** Строка, которая реально рендерится в tbody: заголовок части или строка данных */
type DisplayRow =
    | { kind: 'header', part: ReportPart, repeated: boolean }
    | { kind: 'data', part: ReportPart, row: Report127TableRow }

/** Ключ псевдо-раздела «Все» в переключателе */
const ALL_SECTIONS_KEY = '__all_sections__'

/** «Справочно: Остатки бюджетных средств» — своей строки-итога в данных нет, распознаём по строкам */
const RESIDUALS_TITLE_PREFIX = 'СПРАВОЧНО'
const RESIDUALS_FALLBACK_TITLE = { nameRu: 'Справочно: Остатки бюджетных средств', nameKk: 'Анықтама үшін: Бюджет қаражаттарының қалдықтары' }
const RESIDUALS_ROW_NAMES = new Set([
    'ИСПОЛЬЗУЕМЫЕ ОСТАТКИ БЮДЖЕТНЫХ СРЕДСТВ',
    'ОСТАТКИ БЮДЖЕТНЫХ СРЕДСТВ НА НАЧАЛО ФИНАНСОВОГО ГОДА',
    'ОСТАТКИ БЮДЖЕТНЫХ СРЕДСТВ НА КОНЕЦ ОТЧЕТНОГО ПЕРИОДА',
])

function normalizeName(name: string | null | undefined): string {
    return (name ?? '').replace(/ё/gi, 'е').replace(/\s+/g, ' ').trim().toUpperCase()
}

function splitNameLines(name: string): string[] {
    return name.split('\n').map(it => it.trim()).filter(it => it.length > 0)
}

class TableField {
    key: string
    active: boolean

    income?: string
    expense?: string
    classification?: string

    constructor(
        key: string,
        active: boolean,
        income?: string,
        expense?: string,
        classification?: string
    ) {
        this.key = key
        this.active = active
        this.income = income
        this.expense = expense
        this.classification = classification
    }

    get label(): string {
        const translationKey = 'modules.budget_execution_monitoring.reports_constructor.1_27'

        // For KBK
        if (this.income || this.expense) {
            const incomeLabel: string = this.income ? String(i18n.t(`${translationKey}.${this.income}`)) : ' - '
            const expenseLabel: string = this.expense ? String(i18n.t(`${translationKey}.${this.expense}`)) : ' - '
            const text: string = `${incomeLabel}/${expenseLabel}`
            return text
        }

        const text: string = String(i18n.t(`${translationKey}.${this.key}`))
        return text
    }
}

@Component({
    components: {
        'draggable': VueDraggable,
        'loading': VueElementLoading
    }
})
export default class Report127 extends Vue {

    @Prop({
        type: Array,
        required: true,
    })
    public readonly tableData!: Report127Row[]

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly incomes!: Classificator[]

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly expenses!: Classificator[]

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly propIndicators!: Indicator[]

    @Prop({
        type: String,
        required: true,
        default: '',
    })
    private readonly expenseType!: ExpenseType

    @Prop({
        type: Boolean,
        required: true,
    })
    private readonly isTree!: boolean

    public tableFields: TableField[] = []
    public categoryFields: TableField[] = []
    public indicators: Indicator[] = []

    public readonly pageSize = 100
    public currentPage = 1
    public selectedRegion: Section | null = null
    public selectedSection: Section | null = null
    // Реальные разделы выбранного региона (без «Все»)
    public sectionOptions: Section[] = []
    // В случае если несколько регионов с подробными строками по каждой секций
    public regionSectionOptions: Section[] = []

    public readonly allSectionsOption: Section = {
        key: ALL_SECTIONS_KEY,
        nameRu: '',
        nameKk: '',
        value: [],
        parts: [],
    }

    private indicatorKeys = ['utv', 'utch', 'plg', 'plgp', 'plgo', 'obz', 'nobz', 'sumrg', 'percentage1', 'percentage2']

    public indicatorsNameMap = {
        utv: 'Утвержденный бюджет на отчетный финансовый год',
        utch: 'Уточненный бюджет на отчетный финансовый год',
        plg: 'Скорректированный бюджет на отчетный финансовый год',
        plgs: 'Сводный план поступлений и финансирования по платежам, сводный план финансирования по обязательствам на отчетный период',
            plgp: 'по платежам',
            plgo: 'по обязательствам',
        obz: 'Принятые обязательства',
        nobz: 'Неоплаченные обязательства',
        sumrg: 'Исполнение поступлениий бюджета и/или оплаченных обязательств по бюджетным программам (подпрограммам)',
        percentage1: 'Исп-е поступ-ий бюджета и/или оплач. обяз-в по бюдж. прогр. (подпрогр.)  к свод. плану  поступ-ий и финанс-ия  на отчет. период, %',
        percentage2: 'Исп-е поступ-ий бюджета и/или оплач. обяз-ва по бюдж. прогр. (подпрогр.) к исполняемому бюджету, %',
    }

    i18nService = i18n

    // #region Переключатель разделов

    /** Опции переключателя: «Все» первым пунктом, только если реальных разделов больше одного */
    get sectionSelectOptions(): Section[] {
        if (this.sectionOptions.length > 1) {
            return [this.allSectionsOption, ...this.sectionOptions]
        }
        return this.sectionOptions
    }

    get isAllSectionsSelected(): boolean {
        return this.selectedSection?.key === ALL_SECTIONS_KEY
    }

    /** Разделы, строки которых попадают в таблицу */
    get sectionsInScope(): Section[] {
        if (!this.selectedSection) {
            return []
        }
        return this.isAllSectionsSelected ? this.sectionOptions : [this.selectedSection]
    }

    public sectionLabel(section: Section): string {
        if (section.key === ALL_SECTIONS_KEY) {
            return String(this.$t('modules.budget_execution_monitoring.reports_constructor.all_sections'))
        }
        return this.sectionName(section)
    }

    public sectionName(section: Section): string {
        return this.i18nService.locale === 'kk' ? section.nameKk : section.nameRu
    }

    public partName(part: ReportPart): string {
        return this.i18nService.locale === 'kk' ? part.nameKk : part.nameRu
    }

    // #endregion

    // #region Части отчёта

    /**
     * Делит строки раздела переключателя на части отчёта.
     * - Строка, чьё имя совпадает с одной из строк названия раздела (I. ДОХОДЫ, V., VI., VII.), открывает часть.
     * - Строки остатков бюджетных средств образуют часть «Справочно» (своей строки-итога у неё нет).
     * - Строки до первой распознанной части — часть с именем раздела (обратная совместимость для других шаблонов).
     */
    private buildParts(section: Section): ReportPart[] {
        const rows = section.value as Report127TableRow[]
        const linesRu = splitNameLines(section.nameRu)
        const linesKk = splitNameLines(section.nameKk)
        const normalizedLinesRu = linesRu.map(normalizeName)
        const usedLines = new Set<number>()

        const parts: ReportPart[] = []
        let current: ReportPart | null = null
        let currentIsResiduals = false

        const startPart = (nameRu: string, nameKk: string, hasOwnTitleRow: boolean, isSectionFallback: boolean) => {
            current = {
                key: `${section.key}::${parts.length}`,
                nameRu,
                nameKk,
                hasOwnTitleRow,
                isSectionFallback,
                rows: [],
            }
            parts.push(current)
        }

        rows.forEach(row => {
            const name = normalizeName(row.nameRu)
            const lineIndex = normalizedLinesRu.findIndex((line, i) => line === name && !usedLines.has(i))

            if (lineIndex >= 0) {
                usedLines.add(lineIndex)
                currentIsResiduals = false
                startPart(row.nameRu ?? '', row.nameKk ?? '', true, false)
            } else if (RESIDUALS_ROW_NAMES.has(name) && !currentIsResiduals) {
                const titleIndex = normalizedLinesRu.findIndex(line => line.startsWith(RESIDUALS_TITLE_PREFIX))
                currentIsResiduals = true
                startPart(
                    linesRu[titleIndex] ?? RESIDUALS_FALLBACK_TITLE.nameRu,
                    linesKk[titleIndex] ?? RESIDUALS_FALLBACK_TITLE.nameKk,
                    false,
                    false
                )
            } else if (current === null) {
                startPart(section.nameRu, section.nameKk, false, true)
            }

            current!.rows.push(row)
        })

        return parts
    }

    /**
     * Отдельная строка-заголовок в начале части нужна только для раздела без своей строки-итога при «Все».
     * «Справочно» заголовка в начале не получает — его название появляется только при переносе на след. страницу (см. pageRows).
     */
    private needsInlineHeader(part: ReportPart): boolean {
        return this.isAllSectionsSelected
            && part.isSectionFallback
            && !part.hasOwnTitleRow
            && !!part.nameRu
    }

    // #endregion

    // #region Строки таблицы

    /**
     * Все строки выбранной области (раздел или «Все») после скрытия нулевых.
     * Строки-итоги частей и заголовки не скрываются как нулевые.
     */
    get displayRows(): DisplayRow[] {
        const result: DisplayRow[] = []
        this.sectionsInScope.forEach(section => {
            section.parts.forEach(part => {
                if (this.needsInlineHeader(part)) {
                    result.push({ kind: 'header', part, repeated: false })
                }
                this.filterNonZero(part).forEach(row => {
                    result.push({ kind: 'data', part, row })
                })
            })
        })
        return result
    }

    /** Строки текущей страницы. Если страница начинается с середины части — повторяем её название */
    get pageRows(): DisplayRow[] {
        const startIndex = (this.currentPage - 1) * this.pageSize
        const page = this.displayRows.slice(startIndex, startIndex + this.pageSize)
        const first = page[0]
        const previous = startIndex > 0 ? this.displayRows[startIndex - 1] : undefined
        if (first?.kind === 'data' && previous && previous.part.key === first.part.key) {
            page.unshift({ kind: 'header', part: first.part, repeated: true })
        }
        return page
    }

    /** Ширина строки-заголовка раздела: колонки кодов + наименование + показатели (plgs = 2 колонки) */
    get totalColumnCount(): number {
        const indicatorColumns = this.tableFields
            .filter(it => !['codes', 'name'].includes(it.key))
            .reduce((sum, it) => sum + (it.key === 'plgs' ? 2 : 1), 0)
        return this.categoryFields.length + 1 + indicatorColumns
    }

    private filterNonZero(part: ReportPart): Report127TableRow[] {
        if (this.isTree) {
            return part.rows
        }

        const titleRow = part.hasOwnTitleRow ? part.rows[0] : null
        const activeKeys = this.tableFields.filter(it => it.active && !['codes', 'name'].includes(it.key)).map(it => it.key)
        return part.rows.filter(it => {
            if (it === titleRow) {
                return true
            }
            let hasValues = false
            activeKeys.forEach(key => {
                // @ts-ignore
                if (key in it && it[key]) {
                    hasValues = true
                } else if (key === 'plgs' && (it.plgp || it.plgo)) {
                    hasValues = true
                }
            })
            return hasValues
        })
    }

    public rowKey(item: DisplayRow): string {
        const regionKey = this.selectedRegion?.key ?? ''
        if (item.kind === 'header') {
            return `header-${regionKey}-${item.part.key}${item.repeated ? '-repeat' : ''}`
        }
        return `row-${regionKey}-${item.part.key}-${item.row.index}`
    }

    // #endregion

    get totalColumnSpan(): number {
        let span = 0
        for (const field of this.tableFields) {        
            if (this.indicatorKeys.includes(field.key)) {
                break
            }
            span++
        }
        return span
    }

    get categoriesSpan(): number {
        const activeIncomes = this.incomes.filter(item => item.active)
        const activeExpenses = this.expenses.filter(item => item.active)
        return activeExpenses.length > activeIncomes.length ? activeExpenses.length : activeIncomes.length
    }

    private created() {
        console.log(this.tableData)
        this.indicators = cloneDeep(this.propIndicators)
        this.constructTableFields(true)
        this.fillSections()
    }

    private fillSections() {
        const tempRegionOptions: Section[] = []
        let tempSectionOptions: Section[] = []
        let tempSectionRows: Report127TableRow[] = []
        let tempTableRows = cloneDeep(this.tableData)
        if (
            ('type' in tempTableRows[0] && tempTableRows[0].type !== 'REGION')
            || !('type' in tempTableRows[0])
        ) {
            const separator: SectionSeparator = { nameRu: '', nameKk: '', type: 'REGION' }
            tempTableRows = [separator as Report127Row].concat(tempTableRows)
        }
        if (
            tempTableRows.length >= 2
            && (
                ('type' in tempTableRows[1] && tempTableRows[1].type !== 'SECTION')
                || !('type' in tempTableRows[1])
            )
        ) {
            const separator: SectionSeparator = { nameRu: '', nameKk: '', type: 'SECTION' }
            tempTableRows = [tempTableRows[0]].concat([separator]).concat(tempTableRows.slice(1))
        }
        tempTableRows.forEach(row => {
            if ('type' in row) {
                if (row.type === 'REGION') {
                    tempSectionOptions = []
                    tempRegionOptions.push({
                        key: row.nameRu,
                        value: tempSectionOptions,
                        nameRu: row.nameRu,
                        nameKk: row.nameKk,
                        parts: [],
                    })
                } else if (row.type === 'SECTION') {
                    tempSectionRows = []
                    tempSectionOptions.push({
                        key: row.nameRu,
                        value: tempSectionRows,
                        nameRu: row.nameRu,
                        nameKk: row.nameKk,
                        parts: [],
                    })
                }
            } 
            else {
                tempSectionRows.push(row)
            }
        })

        tempRegionOptions.forEach(region => {
            (region.value as Section[]).forEach(section => {
                section.parts = this.buildParts(section)
            })
        })

        this.regionSectionOptions = tempRegionOptions
        this.selectedRegion = tempRegionOptions[0]
        this.applyRegion(tempRegionOptions[0])
    }

    /** Подставляет разделы региона; по умолчанию выбрано «Все», если разделов больше одного */
    private applyRegion(region: Section) {
        this.sectionOptions = region.value as Section[]
        this.selectedSection = this.sectionOptions.length > 1
            ? this.allSectionsOption
            : this.sectionOptions[0]
        this.currentPage = 1
    }

    private constructTableFields(isOnCreate: boolean) {
        const tempTableFields: TableField[] = []
        tempTableFields.push(new TableField('codes', true))

        let tempIncomes = cloneDeep(this.incomes)
        let tempExpenses = cloneDeep(this.expenses)

        if (tempIncomes.length == 0) {
            tempIncomes = [{ key: 'temp_income', selected: [], active: true }]
        }
        if (tempExpenses.length == 0) {
            tempExpenses = [{ key: 'temp_expenses', selected: [], active: true }]
        }

        const activeIncomes = tempIncomes.filter(item => item.active)
        const activeExpenses = tempExpenses.filter(item => item.active)

        activeExpenses.forEach(it => {
            if (it.key === 'spf') {
                it.key = 'espf'
            }
            if (it.key === 'pcl') {
                it.key = 'epcl'
            }
        })
        
        const categoriesSpan = this.categoriesSpan === 0 ? 1 : this.categoriesSpan
        
        const tempCategoryFields: TableField[] = []
        for (let i = 0; i < categoriesSpan; i++) {
            const incomeKey: string | undefined = activeIncomes[i]?.key
            const expenseKey: string | undefined = activeExpenses[i]?.key

            const field: TableField = new TableField(
                `${incomeKey}/${expenseKey}`,
                true,
                incomeKey,
                expenseKey,
                incomeKey ?? expenseKey
            )
            tempCategoryFields.push(field)
        }
        this.categoryFields = tempCategoryFields

        tempTableFields.push(new TableField('name', true))

        const indicators = isOnCreate ? this.propIndicators : this.indicators

        indicators.filter(it => it.active).forEach(indicator => {
            tempTableFields.push(new TableField(indicator.key, true))
        })

        this.tableFields = tempTableFields
    }

    public handlePageChange(page: number) {
        this.currentPage = page
    }

    public onRegionChange(regionSection: Section) {
        this.applyRegion(regionSection)
    }
    
    public onSectionChange() {
        this.currentPage = 1
    }

    public draggableHeader(columnKey: string) {
        return this.indicatorKeys.includes(columnKey)
    }

    public isTotalColumn(columnKey: string) {
        return ['plan', 'fact', 'delta1'].includes(columnKey)
    }

    public synchronizeIndicatorsOrder() {
        const fullIndicatorKeys = this.indicatorKeys.filter(it => ['plgp', 'plgo'].notIncludes(it)).concat(['plgs'])
        const indicatorsOrder = this.tableFields
            .filter(it => fullIndicatorKeys.includes(it.key))
            .map(it => it.key)

            fullIndicatorKeys.forEach(indicatorKey => {
            if (indicatorsOrder.notIncludes(indicatorKey)) {
                indicatorsOrder.push(indicatorKey)
            }
        })

        const tempIndicators: Indicator[] = []
        indicatorsOrder.forEach(indicatorKey => {
            const IndicatorObject = this.indicators.find(it => it.key === indicatorKey)!
            tempIndicators.push(IndicatorObject)
        })

        this.indicators = tempIndicators
        this.$emit('update-indicators', tempIndicators)
    }

    public synchronizeVisiblityInTable() {
        this.$emit('update-indicators', cloneDeep(this.indicators))
        this.constructTableFields(false)
        // Набор ненулевых строк зависит от видимых показателей — число страниц может измениться
        this.currentPage = 1
    }

    /** Метод-обертка для $t */
    public tr(key: any): string {
        if (typeof key !== 'string') {
            return ''
        }
        const translationKey = 'modules.budget_execution_monitoring.reports_constructor.1_27'
        const text: string = String(this.$t(`${translationKey}.${key}`, 'kk'))
        return text
    }

    public formatDigitsGrouping = formatDigitsGrouping
    public padLeadingZeros = padLeadingZeros
}
